// Central hook to load admin-managed photos from /api/photos and expose
// per-section helpers. Only one HTTP call per mount.
import { useEffect, useState, useMemo, useCallback } from "react";
import { publicApi, fileUrl } from "./adminApi";

// Gallery subcategories – slug ↔ Czech label. Used by both the admin picker
// and the public gallery filter.
export const GALLERY_SUBCATEGORIES = [
  { slug: "vinohrad", label: "Vinohrad" },
  { slug: "degustace", label: "Degustace" },
  { slug: "ubytovani", label: "Ubytování" },
  { slug: "nase-vina", label: "Naše vína" },
  { slug: "vinarstvi", label: "Vinařství" },
  { slug: "zazitky", label: "Zážitky" },
];

const SUB_LABEL_BY_SLUG = Object.fromEntries(GALLERY_SUBCATEGORIES.map((g) => [g.slug, g.label]));

export function galleryLabelFor(slug) {
  return SUB_LABEL_BY_SLUG[slug] || "";
}

// Normalise a photo record → uniform shape used by the UI.
function toItem(p) {
  return {
    id: p.id,
    src: fileUrl(p.id),
    alt: p.alt || "",
    order: p.order || 0,
    target_type: p.target_type,
    target_slug: p.target_slug || "",
  };
}

export function usePhotoOverrides() {
  const [all, setAll] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await publicApi.get("/photos");
      const items = (r.data.photos || []).map(toItem)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      setAll(items);
    } catch (_) {
      setAll([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const api = useMemo(() => ({
    loaded,
    all,
    // Return all photos matching a section (+ optional slug).
    list(target_type, target_slug) {
      return all.filter((p) => p.target_type === target_type && (!target_slug || p.target_slug === target_slug));
    },
    // First image URL for a section (+ optional slug); null when none.
    first(target_type, target_slug) {
      const l = this.list(target_type, target_slug);
      return l.length ? l[0] : null;
    },
    // For public gallery – returns items shaped { src, alt, cat }
    gallery() {
      return all.filter((p) => p.target_type === "gallery").map((p) => ({
        src: p.src, alt: p.alt, cat: galleryLabelFor(p.target_slug) || "Ostatní",
      }));
    },
    reload,
  }), [all, loaded, reload]);

  return api;
}
